import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import Peek from '../components/work/Peek';
import WorkItemTableRow from '../components/work/WorkItemTableRow';
import { PageFrame } from '../components/work/page-chrome';
import { BrandEmpty, ErrorAlert, LoadingList, PageTitle } from '../components/work/page-states';
import { formatFriendlyDateOnly, lateText } from '../lib/dates';
import {
  DueDateCell,
  PriorityLabel,
  ProjectNameCell,
  StateLabel,
  TypeLabel,
  WorkItemTitleLink,
  WorkKeyCell,
} from '../components/work/table-cells';
import { useSession } from '../components/session-context';
import { apiErrorCode, apiErrorMessage, apiFetch, v1Path } from '../lib/api';
import { fetchStatesByProjectIds } from '../lib/project-states';
import type { StateDto } from '../lib/work-types';
import {
  cadenceLabel,
  RISK_VERDICTS,
  isReleaseStatus,
  nextReleaseStatuses,
  releaseStatusLabel,
  releaseStatusNeedsBranch,
  SIGN_OFF_VERDICTS,
  signOffVerdictLabel,
  type CadenceItem,
  type ReleaseFull,
} from '../lib/releases';
import { wikiRoute } from '../lib/wiki';

const UNSET_VERDICT = '__unset__';

export default function ReleaseDetail() {
  const { releaseId } = useParams();
  const navigate = useNavigate();
  const { isOrgAdmin } = useSession();
  const [release, setRelease] = useState<ReleaseFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stateById, setStateById] = useState<Map<string, StateDto>>(() => new Map());
  const branchRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!releaseId) return;
    setLoading(true);
    setError(null);
    try {
      setRelease(await apiFetch<ReleaseFull>(v1Path(`/releases/${releaseId}`)));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load release'));
      setRelease(null);
    } finally {
      setLoading(false);
    }
  }, [releaseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!release) {
      setStateById(new Map());
      return;
    }
    const projectIds = Array.from(new Set(release.items.map((item) => item.project_id)));
    if (projectIds.length === 0) {
      setStateById(new Map());
      return;
    }
    let cancelled = false;
    fetchStatesByProjectIds(projectIds)
      .then(({ byId }) => {
        if (!cancelled) setStateById(byId);
      })
      .catch(() => {
        if (!cancelled) setStateById(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [release]);

  async function patch(fields: Record<string, unknown>): Promise<void> {
    if (!release) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiFetch<ReleaseFull>(v1Path(`/releases/${release.id}`), {
        method: 'PATCH',
        body: JSON.stringify({ version: release.version, ...fields }),
      });
      setRelease({
        ...updated,
        items: release.items,
        hidden_item_count: release.hidden_item_count,
        cadence: release.cadence,
        wiki_slug: release.wiki_slug,
      });
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to update release');
      if (apiErrorCode(err) === 'STALE_VERSION') {
        await load();
      }
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(): Promise<void> {
    if (!release) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch(v1Path(`/releases/${release.id}`), { method: 'DELETE' });
      navigate('/releases');
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to delete release'));
      setSaving(false);
    }
  }

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col">
      <p className="mb-2 text-xs text-muted-foreground">
        <Link to="/releases" className="hover:underline">
          Releases
        </Link>
      </p>
      {error ? <ErrorAlert>{error}</ErrorAlert> : null}
      {loading || !release ? (
        loading ? <LoadingList /> : null
      ) : (
        <>
          <PageTitle
            title={release.name}
            count={release.item_count}
            action={
              isOrgAdmin && release.status === 'draft' ? (
                <Button type="button" size="sm" variant="outline" disabled={saving} onClick={() => void onDelete()}>
                  Delete draft
                </Button>
              ) : null
            }
          />
          <ReleaseSummary
            status={releaseStatusLabel(release.status)}
            released={release.status === 'released'}
            targetDate={release.target_date ?? null}
            total={release.items.length}
            done={release.items.filter((item) => stateById.get(item.state_id)?.group === 'completed').length}
          />
          <details
            className="group mb-8 rounded-lg border px-4 py-3"
            open={isOrgAdmin && release.status === 'draft' ? true : undefined}
          >
            <summary className="cursor-pointer select-none text-sm font-medium text-muted-foreground hover:text-foreground">
              Release settings
              <span className="ml-2 text-xs font-normal">status, base branch, tag, target date</span>
            </summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-4">
            <div>
              <Label htmlFor="rel-status">Status</Label>
              <Select
                value={release.status}
                disabled={!isOrgAdmin || saving || release.status === 'released'}
                onValueChange={(value) => {
                  if (!isReleaseStatus(value) || value === release.status) return;
                  const typed = branchRef.current?.value.trim() || null;
                  if (releaseStatusNeedsBranch(value) && !typed) {
                    setError('Set a base branch before leaving draft.');
                    return;
                  }
                  const fields: Record<string, unknown> = { status: value };
                  if (typed !== (release.branch_name ?? null)) fields.branch_name = typed;
                  void patch(fields);
                }}
              >
                <SelectTrigger id="rel-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {nextReleaseStatuses(release.status).map((status) => (
                    <SelectItem key={status} value={status}>
                      {releaseStatusLabel(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {release.status === 'draft' && !release.branch_name ? (
                <p className="mt-1 text-xs text-muted-foreground">Set a base branch before leaving draft.</p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="rel-branch">Base branch</Label>
              <Input
                id="rel-branch"
                ref={branchRef}
                defaultValue={release.branch_name ?? ''}
                disabled={!isOrgAdmin || saving}
                onBlur={(event) => {
                  const next = event.target.value.trim() || null;
                  if (next !== (release.branch_name ?? null)) void patch({ branch_name: next });
                }}
              />
            </div>
            <div>
              <Label htmlFor="rel-tag">Tag</Label>
              <Input
                id="rel-tag"
                defaultValue={release.tag ?? ''}
                disabled={!isOrgAdmin || saving}
                onBlur={(event) => {
                  const next = event.target.value.trim() || null;
                  if (next !== (release.tag ?? null)) void patch({ tag: next });
                }}
              />
            </div>
            <div>
              <Label htmlFor="rel-date">Target date</Label>
              <Input
                id="rel-date"
                type="date"
                defaultValue={release.target_date ?? ''}
                disabled={!isOrgAdmin || saving}
                onBlur={(event) => {
                  const next = event.target.value || null;
                  if (next !== (release.target_date ?? null)) void patch({ target_date: next });
                }}
              />
            </div>
          </div>
          </details>
          <h2 className="mb-2 text-sm font-semibold">What's in it</h2>
          {release.hidden_item_count > 0 ? (
            <p className="mb-4 text-xs text-muted-foreground">
              {release.hidden_item_count} {release.hidden_item_count === 1 ? 'item is' : 'items are'} in
              streams you cannot see.
            </p>
          ) : null}
          {release.items.length === 0 ? (
            <BrandEmpty
              title="Nothing in this release yet."
              description="Open a task and pick this release to add it."
            />
          ) : (
            <>
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[7.5rem]">Key</TableHead>
                    <TableHead className="min-w-0 w-[38%]">Title</TableHead>
                    <TableHead className="hidden w-[10rem] md:table-cell">Stream</TableHead>
                    <TableHead className="hidden w-28 md:table-cell">Priority</TableHead>
                    <TableHead className="hidden w-32 md:table-cell">Due date</TableHead>
                    <TableHead className="hidden w-[9rem] md:table-cell">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <AnimatePresence initial={false}>
                    {release.items.map((item) => {
                      const state = stateById.get(item.state_id);
                      return (
                        <WorkItemTableRow key={item.id} workItemId={item.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <WorkKeyCell
                                workItemId={item.id}
                                workItemKey={item.key}
                                projectId={item.project_id}
                              />
                              <TypeLabel type={item.type} />
                            </div>
                          </TableCell>
                          <TableCell className="min-w-0">
                            <div className="truncate" title={item.title}>
                              <WorkItemTitleLink
                                workItemId={item.id}
                                workItemKey={item.key}
                                projectId={item.project_id}
                                title={item.title}
                              />
                            </div>
                            <span className="mt-0.5 flex items-center gap-2 md:hidden">
                              <PriorityLabel priority={item.priority} />
                              <StateLabel
                                name={state?.name ?? item.state_name}
                                color={state?.color}
                                group={state?.group}
                              />
                            </span>
                          </TableCell>
                          <TableCell className="hidden min-w-0 md:table-cell">
                            <ProjectNameCell
                              name={item.project_name}
                              workItemKey={item.key}
                              projectId={item.project_id}
                            />
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <PriorityLabel priority={item.priority} />
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <DueDateCell value={item.due_date} done={state?.group === 'completed'} />
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <StateLabel
                              name={state?.name ?? item.state_name}
                              color={state?.color}
                              group={state?.group}
                            />
                          </TableCell>
                        </WorkItemTableRow>
                      );
                    })}
                  </AnimatePresence>
                </TableBody>
              </Table>
              <Peek itemKeys={release.items.map((item) => item.key)} />
            </>
          )}
          <CadenceBlock
            releaseId={release.id}
            rows={release.cadence}
            disabled={saving}
            onError={setError}
            onRow={(row) =>
              setRelease({
                ...release,
                cadence: release.cadence.map((item) => (item.kind === row.kind ? row : item)),
              })
            }
          />
        </>
      )}
      </div>
    </PageFrame>
  );
}

function ReleaseSummary({
  status,
  released,
  targetDate,
  total,
  done,
}: {
  status: string;
  released: boolean;
  targetDate: string | null;
  total: number;
  done: number;
}) {
  const today = new Date();
  const when = targetDate ? formatFriendlyDateOnly(targetDate, today) : null;
  const late = !released && done < total ? lateText(targetDate, today) : null;
  return (
    <p className="mb-4 text-sm">
      <span className="font-medium">{status}.</span>{' '}
      {when ? (
        <>
          Planned for <span className="font-medium">{when}</span>
          {late ? <span className="font-medium text-red-600 dark:text-red-400"> ({late})</span> : null}.{' '}
        </>
      ) : (
        'No date yet. '
      )}
      {total === 0 ? 'Nothing is in it yet.' : `${done} of ${total} ${total === 1 ? 'thing' : 'things'} in it ${done === 1 ? 'is' : 'are'} done.`}
    </p>
  );
}

function CadenceBlock({
  releaseId,
  rows,
  disabled,
  onError,
  onRow,
}: {
  releaseId: string;
  rows: CadenceItem[];
  disabled: boolean;
  onError: (message: string | null) => void;
  onRow: (row: CadenceItem) => void;
}) {
  const [savingKind, setSavingKind] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function patchRow(row: CadenceItem, fields: Record<string, unknown>): Promise<void> {
    setSavingKind(row.kind);
    onError(null);
    try {
      const updated = await apiFetch<CadenceItem>(v1Path(`/releases/${releaseId}/cadence/${row.kind}`), {
        method: 'PATCH',
        body: JSON.stringify({ version: row.version, ...fields }),
      });
      onRow(updated);
    } catch (err) {
      onError(apiErrorMessage(err, 'Failed to update cadence'));
    } finally {
      setSavingKind(null);
    }
  }

  const doneCount = rows.filter((row) => row.status === 'done').length;
  const lead = rows.filter((row) => row.kind === 'risk' || row.kind === 'sign_off');
  const rest = rows.filter((row) => row.kind !== 'risk' && row.kind !== 'sign_off');

  function cadenceItem(row: CadenceItem) {
    const busy = disabled || savingKind === row.kind;
    const href = wikiRoute({ kind: 'org', projectId: null }, row.wiki_path);
    return (
      <li key={row.kind} className="min-w-0">
        <div className="flex items-center gap-2">
          {row.kind === 'risk' ? (
            <Select
              value={row.verdict ?? UNSET_VERDICT}
              disabled={busy}
              onValueChange={(value) => {
                void patchRow(
                  row,
                  value === UNSET_VERDICT ? { status: 'todo', verdict: null } : { verdict: value },
                );
              }}
            >
              <SelectTrigger className="w-[6.5rem]" aria-label="Risk verdict">
                <SelectValue placeholder="Risk" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNSET_VERDICT}>Not set</SelectItem>
                {RISK_VERDICTS.map((verdict) => (
                  <SelectItem key={verdict} value={verdict}>
                    {verdict}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : row.kind === 'sign_off' ? (
            <Select
              value={row.verdict ?? UNSET_VERDICT}
              disabled={busy}
              onValueChange={(value) => {
                void patchRow(
                  row,
                  value === UNSET_VERDICT ? { status: 'todo', verdict: null } : { verdict: value },
                );
              }}
            >
              <SelectTrigger className="w-[9.5rem]" aria-label="Sign-off verdict">
                <SelectValue placeholder="Sign-off" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNSET_VERDICT}>Not set</SelectItem>
                {SIGN_OFF_VERDICTS.map((verdict) => (
                  <SelectItem key={verdict} value={verdict}>
                    {signOffVerdictLabel(verdict)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Checkbox
              checked={row.status === 'done'}
              disabled={busy}
              aria-label={`${cadenceLabel(row.kind)} done`}
              onCheckedChange={(checked) => {
                void patchRow(row, { status: checked === true ? 'done' : 'todo' });
              }}
            />
          )}
          <Link to={href} className="min-w-0 truncate text-sm text-primary underline underline-offset-2">
            {cadenceLabel(row.kind)}
          </Link>
        </div>
      </li>
    );
  }

  return (
    <section className="mt-10 border-t pt-6">
      <button
        type="button"
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
        <span>Service Quality & Reliability</span>
        <span className="font-mono text-xs">
          {doneCount}/{rows.length} done
        </span>
      </button>
      {open ? (
        <div className="mt-3 space-y-3">
          <ul className="grid gap-x-8 gap-y-2 sm:grid-cols-2">{lead.map(cadenceItem)}</ul>
          <ul className="grid gap-x-8 gap-y-2 sm:grid-cols-2">{rest.map(cadenceItem)}</ul>
        </div>
      ) : null}
    </section>
  );
}
